// @flow

import { Config } from '../util/config'
import { getMaskAsUserId } from './maskAsUser'
import { loadScript } from '../util/browser'
import * as React from 'react'
import * as Storage from '../util/storage'
import CATerritories from '../assets/location/canadianTerritories.json'
import Countries from '../assets/location/countryData.json'
import DeleteIcon from 'material-ui/svg-icons/action/delete'
import geolocator from 'geolocator'
import pick from 'lodash.pick'
import ShareIcon from 'material-ui/svg-icons/social/people'
import USStates from '../assets/location/usStates.json'

import type { SOM } from '../ducks/user/types'

/**
 * Utility functions related locations
 * @module util/location
 */

// Types //////////////////////////////////////////////////////////////////////

/* eslint-disable no-undef */
export type Country = $Keys<typeof Countries>
/* eslint-enable no-undef */

export type GeoPoint = {|
  latitude: number,
  longitude: number
|}

export type WeatherStation = {|
  city: string,
  country: string,
  distance: number,
  elevation: number,
  geoPoint: GeoPoint,
  moreInfoUrl: string,
  hasPrecip: boolean,
  state: string, // Lowercased abbreviation
  stationId: string,
  stationType: 'NATIONAL' | 'PERSONAL'
|}

export type AbbrGeoPoint = {
  lat: number,
  lng: number
}

// Storage ////////////////////////////////////////////////////////////////////

/**
 * Create a key to store/retrieve a user's last accessed location ID
 * @param {string} userId - User ID
 * @returns {string} - Key to work with user's last location ID
 */
const getSavedLocationKey = (userId: string): string =>
  `rachio:user:${userId}:lastLocationId`

/**
 * Save a user's location ID
 * @param {string} userId - User ID
 * @param {string} locId - Location ID
 * @returns {boolean} - Whether or not the location ID was preserved
 */
export const saveLocationId = (userId: string, locId: string): boolean => {
  /** Whether or not the current user is masquerading as another user */
  const isMasking: boolean = Boolean(getMaskAsUserId())

  // Don't save location IDs if this isn't the actual user
  if (!isMasking) {
    const key: string = getSavedLocationKey(userId)
    return Storage.set(key, locId)
  } else {
    return false
  }
}

/**
 * Retrieve a user's last used location ID
 * @param {string} userId - User ID
 * @returns {null|string} - The user's last used location ID, if present
 */
export const getSavedLocationId = (userId: string): ?string => {
  const key: string = getSavedLocationKey(userId)
  return Storage.get(key)
}

// Countries //////////////////////////////////////////////////////////////////

/**
 * Get object representing country
 * @param  {string} code   Country code
 * @param  {string[]} [filter] Subset of properties to return
 * @return {object} - Object of country data
 */
export const getCountryObjByCode = (code: Country, filter: Array<string>) => {
  let countryObj = { code: code, ...Countries[code] }
  if (filter) {
    countryObj = pick(countryObj, filter)
  }
  return countryObj
}

export const getCountryArr = (): Array<Object> => {
  const countryArr = []
  Object.keys(Countries).forEach(countryCode =>
    countryArr.push({ code: countryCode, ...Countries[countryCode] })
  )
  return countryArr
}

// States /////////////////////////////////////////////////////////////////////

type StateObject = {|
  name: string,
  code: string
|}

export const getStateObjsByCountryCode = (code: Country): ?{} => {
  switch (code) {
    case 'US':
      return USStates
    case 'CA':
      return CATerritories
    default:
      return null
  }
}

/**
 * Get state object from state and country codes
 * @param  {string} countryCode - Country code
 * @param  {string} regionCode - state/Region code
 * @return {object} - State object (object with name and code properties)
 */
export const getStateObj = (
  countryCode: Country,
  regionCode: string
): StateObject => {
  const allRegionsByCountry = getStateObjsByCountryCode(countryCode) || {}
  const stateCode = { code: regionCode, name: allRegionsByCountry[regionCode] }
  return stateCode
}

export const getStateObjArrByCountryCode = (
  code: Country
): ?Array<StateObject> => {
  const statesObj = getStateObjsByCountryCode(code)
  const statesArr = []
  if (statesObj) {
    Object.keys(statesObj).forEach(stateCode =>
      statesArr.push({ code: stateCode, name: statesObj[stateCode] })
    )
    return statesArr
  } else {
    return null
  }
}

// Models /////////////////////////////////////////////////////////////////////

/**
 * Build Update Request
 * @param {object} exsitingLocation - Location to update
 * @param {object} updates - Upodate data
 * @returns {object} - API compatable location update object
 */
export const buildUpdateRequest = (
  exsitingLocation: Object,
  updates: Object
) => {
  const el = Object.assign({}, exsitingLocation)
  const request = {
    locationId: el.id,
    name: updates.name || el.name,
    address: { ...el.address, ...updates.address },
    deviceIdsToAdd: [],
    deviceIdsToRemove: [],
    geoPoint: updates.geoPoint || el.geoPoint
  }

  return request
}

// Geolocation ////////////////////////////////////////////////////////////////

// Type defs
type GeoLocatorLocation = {
  coords: {
    latitude: number,
    longitude: number
  },
  address: {
    commonName: string,
    street: string,
    route: string,
    streetNumber: string,
    neighborhood: string,
    town: string,
    city: string,
    region: string,
    state: string,
    stateCode: string,
    postalCode: string,
    country: string,
    countryCode: string
  }
}

// See: https://onury.io/geolocator/?api=geolocator#geolocator.DistanceFormula
type GeolocatorDistanceFormula = 'haversine' | 'pythagorean'

// See: https://onury.io/geolocator/?api=geolocator#geolocator.UnitSystem
type GeolocatorUnitSystem = 0 | 1

// `geolocator`` config
geolocator.config({
  language: 'en',
  google: {
    version: '3',
    key: Config.GOOGLE_MAPS_API_KEY
  }
})

type GeolocatorOptions = {|
  enableHighAccuracy?: boolean,
  timeout?: number,
  maximumWait?: number,
  maximumAge?: number,
  desiredAccuracy?: number,
  fallbackToIP?: boolean,
  addressLookup?: boolean
|}

/** Default `geolocator` options */
const defaultGeolocatorOptions: GeolocatorOptions = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumWait: 10000,
  maximumAge: 0,
  desiredAccuracy: 30,
  fallbackToIP: true,
  addressLookup: true
}

/**
 * Get the app's current location
 * @see {@link https://github.com/onury/geolocator}
 * @param  {object} options - Options for `geolocator`
 * @return {Promise} - Geolocator callback
 */
export const getCurrentLocation = (
  options?: ?GeolocatorOptions
): Promise<GeoLocatorLocation> => {
  return new Promise((resolve, reject) => {
    geolocator.locate(
      { ...defaultGeolocatorOptions, ...options },
      (err, location) => {
        if (err) {
          reject(err)
        } else {
          resolve(location)
        }
      }
    )
  })
}

const defaultGeolocatorWatchOptions = {
  enableHighAccuracy: true,
  timeout: 4000,
  maximumAge: 0
}

/* eslint-disable max-len */
/**
 * Get the app's current location
 * @see {@link https://github.com/onury/geolocator}
 * @param {locationCallback} callback - A callback
 * @param  {object} options - Options for `geolocator`
 * @return {Promise} - Geolocator callback
 */
export const watchCurrentLocation = (
  callback: GeoLocatorLocation => void,
  options: {} = {}
): Promise<GeoLocatorLocation> => {
  /* eslint-enable max-len */
  return geolocator.watch(
    { ...defaultGeolocatorWatchOptions, ...options },
    (err, location) => {
      if (location) {
        callback(location)
      }
    }
  )
}

/**
 * Get latitude and longitude from street, city, and state
 * @param  {string} street - Street (w/ number)
 * @param  {string} city - City
 * @param  {string} state - State
 * @param {string} zip - Zip
 * @param {string} country - Country
 * @return {Promise} - An object with { lat, lng }
 */
export const getLatLngAttempt = (
  street?: string = '',
  city?: string = '',
  state?: string = '',
  zip?: string = '',
  country: string = ''
): Promise<{ lat: number, lng: number, addressAccuracy: ?string }> => {
  return new Promise((resolve, reject) => {
    /** Whether or not the required lib is loaded */
    const isScriptLoaded: boolean = Boolean(window.google && window.google.maps)

    /** Functionality to execute once li is loaded */
    const onScriptLoad = () => {
      const geocoder = new window.google.maps.Geocoder()
      geocoder.geocode({ address: addressStr }, function(results, status) {
        if (status == 'OK') {
          parseResults(results)
        } else {
          alert(
            'Geocode was not successful for the following reason: ' + status
          )
          reject(new Error(status))
        }
      })
    }

    /**
     * Parse results from geoCoding service
     * @param  {object[]} results - Results from geoCoding service
     */
    const parseResults = results => {
      if (results[0]) {
        resolve({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
          addressAccuracy: getTypeOfGoogleAddress(results[0].types)
        })
      } else {
        reject(new Error('NO_RESULTS'))
      }
    }

    /**
     * Replace spaces in string with `+`
     * @param {string} str - String
     * @return {string} - String with spaces replaced
     */
    const swapSpaces = (str: string): string =>
      str
        .trim()
        .split(' ')
        .join('+')

    /** Address in a submittable format */
    const addressStr: string =
      (street ? swapSpaces(street) + ',' : '') +
      (city ? swapSpaces(city) + ',' : '') +
      (state ? swapSpaces(state) + ',' : '') +
      (zip ? swapSpaces(zip) + ',' : '') +
      swapSpaces(country)

    if (isScriptLoaded) {
      onScriptLoad()
    } else {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${Config.GOOGLE_MAPS_API_KEY}`,
        { async: true },
        {
          load: onScriptLoad
        }
      )
    }
  })
}

export const getLatLngFromAddress = (
  street: string = '',
  city: string = '',
  state: string = '',
  zip: string = '',
  country: string = ''
): Promise<{ lat: number, lng: number, addressAccuracy: ?string }> => {
  return getLatLngAttempt(street, city, state, zip, country)
}

export const meterBoundsForAccuracyType = {
  COUNTRY: 2000 /* miles */ * 1609.34,
  STATE: 200 /* miles */ * 1609.34,
  CITY: 15 /* miles */ * 1609.34
}

/**
 * Get most accurate type from type array returned from Google geolocator results
 * @see {@link https://developers.google.com/maps/documentation/geocoding/intro#Types}
 * @param  {string[]} types - Array of types
 * @return {string|null} - Most accurate type
 */
export const getTypeOfGoogleAddress = (
  types: Array<string>
): ?('STREET' | 'CITY' | 'STATE' | 'COUNTRY') => {
  if (!types) return null
  let type = null

  if (
    types.includes('premise') ||
    types.includes('subpremise') ||
    types.includes('street_address')
  ) {
    type = 'STREET'
  } else if (types.includes('locality')) {
    type = 'CITY'
  } else if (types.includes('administrative_area_level_1')) {
    type = 'STATE'
  } else if (types.includes('country')) {
    type = 'COUNTRY'
  }

  return type
}

/**
 * Get distance between 2 `GeoPoint``s
 * @param {object} from - GeoPoint 1
 * @param {object} to - GeoPoint 2
 * @param {string} formula - Distance formula
 * @param {string} systemOfMeasurement - Imperial/Metric
 * @returns {number} - Distance
 */
export const getDistance = (
  from: GeoPoint,
  to: GeoPoint,
  formula: ?GeolocatorDistanceFormula = 'pythagorean',
  systemOfMeasurement: ?GeolocatorUnitSystem
): number =>
  geolocator.calcDistance({
    from,
    to,
    formula,
    unitSystem: systemOfMeasurement
  })

/**
 * Convert a user's SOM to the type of SOM recognized by geolocator
 * @param  {'IMPERIAL'| 'METRIC'} strVal - User's SOM
 * @return {*} - Geolocator version of user's SOM
 */
export const getGeolocatorSOM = (strVal: SOM) => {
  if (strVal === 'IMPERIAL') {
    return geolocator.UnitSystem.IMPERIAL
  } else if (strVal === 'METRIC') {
    return geolocator.UnitSystem.METRIC
  }
}

// Sharing ////////////////////////////////////////////////////////////////////

export const isShareCode = (code: string) => /^SHARE_/.test(code)

// Builders ///////////////////////////////////////////////////////////////////

import typeof QuickActionItem from '../components/QuickActions'

type createQuickActionArrayArgs = {
  onEditTouchTap?: () => void,
  onRemoveTouchTap: () => void,
  onShareTouchTap: () => void,
  onRemoveSharedTouchTap: () => void
}
/**
 * Create an array of quick action items
 * @param  {object} actionHandlers - Handlers for menu item touchTaps
 * @return {object[]} - Quick action items
 */
export const createQuickActionArray = (
  actionHandlers: createQuickActionArrayArgs
): Array<QuickActionItem> => {
  const quickActionArr = []

  if (actionHandlers.onShareTouchTap) {
    quickActionArr.push({
      primaryText: 'Share Controller',
      leftIcon: <ShareIcon />,
      onClick: actionHandlers.onShareTouchTap
    })
  }
  if (actionHandlers.onRemoveTouchTap) {
    quickActionArr.push({
      primaryText: 'Remove Controller',
      leftIcon: <DeleteIcon />,
      onClick: actionHandlers.onRemoveTouchTap
    })
  }
  if (actionHandlers.onRemoveSharedTouchTap) {
    quickActionArr.push({
      primaryText: 'Remove Shared Controller',
      leftIcon: <DeleteIcon />,
      onClick: actionHandlers.onRemoveSharedTouchTap
    })
  }

  // $FlowFixMe
  return quickActionArr
}

// Sharing ////////////////////////////////////////////////////////////////////

/**
 * Determine if a location is shared with the current user
 * @param {object} shareModel - An object with an `owner` key
 * @returns {bool} - Whether or not the thing is shared
 */
export const isSharedWithCurrentUser = (shareModel: {
  owner: boolean
}): boolean => shareModel && shareModel.owner !== true
